/**
 * The legacy identifier that names a CMMD cloud runtime: the only place Forge
 * spells its prefix, and the only place that constructs one.
 *
 * CMMD defines the same format on its side of the wire in
 * `server/services/forge/forge-runtime-identity.ts`. An Environment now has an
 * identifier of its own there (`env_…`, FOR-32643) which outlives the Sprite
 * backing it; this form does not, because a repair mints a fresh runtime id.
 * FOR-32652 retires it. Retiring it means deleting this module and the
 * `legacy-runtime` branch of `environmentIdClassification.ts`, which imports
 * the prefix from here and is where the format is interpreted. The test next
 * to this file keeps the literal from spreading anywhere else.
 *
 * The three classification predicates that used to live here migrated to
 * `classifyEnvironmentId` in `environmentIdClassification.ts` and were
 * deleted in #3353. What remains here is only the format itself: the prefix,
 * its type, and the one function that builds an id in it.
 * `environmentIdClassification.ts` imports the prefix from here; this module
 * imports nothing back, so the cycle the two modules held during the
 * migration is gone.
 */

export const CMMD_RUNTIME_ENVIRONMENT_PREFIX = "cmmd-runtime:";

/**
 * The shape of a legacy cloud environment identifier.
 *
 * Exported because a persisted contract row declares its `environmentId` field
 * with this shape, and a hand-written template-literal type in that row would
 * be one more copy of the format to retire.
 */
export type CmmdRuntimeEnvironmentId = `${typeof CMMD_RUNTIME_ENVIRONMENT_PREFIX}${string}`;

/**
 * The environment id that names a runtime.
 *
 * The counterpart of `environmentIdForRuntime` in CMMD's identity module. Five
 * call sites built this string inline before FOR-32647; a template literal in a
 * component is not a place the format can be retired from.
 */
export function buildCmmdRuntimeEnvironmentId(runtimeId: string): CmmdRuntimeEnvironmentId {
  return `${CMMD_RUNTIME_ENVIRONMENT_PREFIX}${runtimeId}`;
}
